import { lazy, Suspense, type ReactNode } from 'react'
import { Navigate, Route, Routes, useParams, useSearchParams } from 'react-router-dom'
import { PageContainer } from '@/atoms/page-container'
import { Spinner } from '@/atoms/spinner'
import { useAuth } from '../hooks/useAuth'
import { AppShellView } from './AppShellView'
import { AuthCallbackView } from './AuthCallbackView'
import { DiscordLinkView } from './DiscordLinkView'
import { DiscordPageView } from './DiscordPageView'
import { InviteView } from './InviteView'
import { LandingView } from './LandingView'
import { MemeDetailView } from './MemeDetailView'
import { MobileAuthForwardView } from './MobileAuthForwardView'
import { PrivacyView } from './PrivacyView'
import { ProfileView } from './ProfileView'
import { TermsView } from './TermsView'

/* Landing, legal and the public share routes stay eager so first paint is unchanged;
   everything only a signed-in player can reach arrives with its route. */
const BinderView = lazy(() => import('./BinderView').then((m) => ({ default: m.BinderView })))
const CreateMemeView = lazy(() =>
  import('./CreateMemeView').then((m) => ({ default: m.CreateMemeView })),
)
const DevelopersView = lazy(() =>
  import('./DevelopersView').then((m) => ({ default: m.DevelopersView })),
)
const FriendsView = lazy(() => import('./FriendsView').then((m) => ({ default: m.FriendsView })))
const LeaderboardView = lazy(() =>
  import('./LeaderboardView').then((m) => ({ default: m.LeaderboardView })),
)
const MarketplaceView = lazy(() =>
  import('./MarketplaceView').then((m) => ({ default: m.MarketplaceView })),
)
const SettingsView = lazy(() => import('./SettingsView').then((m) => ({ default: m.SettingsView })))
const TradesView = lazy(() => import('./TradesView').then((m) => ({ default: m.TradesView })))

function LegacyMemeRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/m/${id}`} replace />
}

/** Bare /binder → the caller's own shareable /binder/:sub URL. */
function BinderOwnRedirect() {
  const { user } = useAuth()
  return <Navigate to={`/binder/${encodeURIComponent(user!.sub)}`} replace />
}

/** /binder/:sub — owner gets the management binder; anyone else gets the public profile binder. */
function BinderRoute() {
  const { sub } = useParams<{ sub: string }>()
  const { user, loading, checkingSessionLabel } = useAuth()
  if (loading) return <AuthSpinner label={checkingSessionLabel} />
  if (user && sub === user.sub)
    return (
      <Suspense fallback={<AuthSpinner label={checkingSessionLabel} />}>
        <BinderView />
      </Suspense>
    )
  return <ProfileView key={sub} initialTab="binder" />
}

function ProfileRoute() {
  const { sub } = useParams<{ sub: string }>()
  return <ProfileView key={sub} />
}

/** Fresh machine per meme id so remix/cap-table links do not reuse the previous actor. */
function MemeDetailRoute() {
  const { id } = useParams<{ id: string }>()
  return <MemeDetailView key={id} />
}

/** A remix source is actor input, so changing it needs a fresh mint engine. */
export function CreateMemeRoute() {
  const [params] = useSearchParams()
  const remixId = params.get('remix')
  return <CreateMemeView key={remixId ?? ''} />
}

/** An inviter is actor input, so changing it needs a fresh invite engine. */
export function InviteRoute() {
  const { sub } = useParams<{ sub: string }>()
  return <InviteView key={sub} />
}

/** The one named waiting state for a guarded route: auth resolving, then the chunk arriving. */
function AuthSpinner({ label }: { label: string }) {
  return (
    <PageContainer as="main" id="main" tabIndex={-1}>
      {/* a labelled spinner row, never a bare spinner */}
      <div
        data-slot="loading-state"
        className="flex items-center justify-center gap-2.5 px-5 py-15 text-sm text-muted-foreground"
        role="status"
      >
        <Spinner />
        {label}
      </div>
    </PageContainer>
  )
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading, checkingSessionLabel } = useAuth()
  if (loading) return <AuthSpinner label={checkingSessionLabel} />
  if (!user) return <Navigate to="/" replace />
  return <Suspense fallback={<AuthSpinner label={checkingSessionLabel} />}>{children}</Suspense>
}

export function AppView() {
  return (
    <AppShellView>
      <Routes>
        <Route path="/" element={<LandingView />} />
        <Route path="/auth/callback" element={<AuthCallbackView />} />
        <Route path="/auth/mobile" element={<MobileAuthForwardView />} />
        <Route path="/invite/:sub" element={<InviteRoute />} />
        <Route path="/discord" element={<DiscordPageView />} />
        <Route path="/discord/link" element={<DiscordLinkView />} />
        <Route path="/privacy" element={<PrivacyView />} />
        <Route
          path="/developers"
          element={
            <RequireAuth>
              <DevelopersView />
            </RequireAuth>
          }
        />
        <Route path="/terms" element={<TermsView />} />
        <Route
          path="/marketplace"
          element={
            <RequireAuth>
              <MarketplaceView />
            </RequireAuth>
          }
        />
        <Route
          path="/binder"
          element={
            <RequireAuth>
              <BinderOwnRedirect />
            </RequireAuth>
          }
        />
        {/* public: shared binder links must work for logged-out visitors */}
        <Route path="/binder/:sub" element={<BinderRoute />} />
        <Route
          path="/binder/new"
          element={
            <RequireAuth>
              <CreateMemeRoute />
            </RequireAuth>
          }
        />
        <Route
          path="/friends"
          element={
            <RequireAuth>
              <FriendsView />
            </RequireAuth>
          }
        />
        <Route
          path="/trade"
          element={
            <RequireAuth>
              <TradesView />
            </RequireAuth>
          }
        />
        <Route
          path="/leaderboard"
          element={
            <RequireAuth>
              <LeaderboardView />
            </RequireAuth>
          }
        />
        {/* the account menu's Settings row lands here at every width */}
        <Route
          path="/settings"
          element={
            <RequireAuth>
              <SettingsView />
            </RequireAuth>
          }
        />
        {/* public: profile links unfurl with og cards, so they must load logged-out too */}
        <Route path="/u/:sub" element={<ProfileRoute />} />
        {/* /m/ is the one true meme URL; legacy /meme/ links redirect into it */}
        <Route path="/m/:id" element={<MemeDetailRoute />} />
        <Route path="/meme/:id" element={<LegacyMemeRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShellView>
  )
}
